import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faCircle, faFlag, faTimes } from '@fortawesome/free-solid-svg-icons';
import './QuestionNavigationSidebar.css';

/**
 * Question navigation sidebar component
 * @param {Object} props Component props
 */
const QuestionNavigationSidebar = ({
  questions = [],
  responses = [],
  currentQuestionId,
  goToQuestion,
  isDarkMode,
  themeStyles,
  sidebarPage = 0,
  questionsPerPage = 20,
  totalSidebarPages = 1,
  onPrevPage,
  onNextPage,
  toggleSidebar,
}) => {  
  const isMobile = window.innerWidth < 768;
  // Function to determine question status icon and color
  const getQuestionStatus = (question) => {
    if (!question) return {
      icon: faCircle,
      color: '#6B7280' // Light gray for never answered
    };
    
    const response = responses.find(r => r.questionId === question.id);
    
    if (!response) {
      return {
        icon: faCircle,
        color: '#6B7280' // Light gray for never answered
      };
    }
      // Check if question has been answered (has answerIds)
    if (response.answerIds && response.answerIds.length > 0) {
      return response.isCorrect === true ? {
        icon: faCheckCircle,
        color: '#059669' // Green for correct answers
      } : {
        icon: faTimesCircle,
        color: '#EF4444' // Red for incorrect answers
      };
    }
    
    // Check if question was skipped
    if (response.isSkipped) {
      // 🎯 FIX: In practice mode, skipped questions should show gray
      return {
        icon: faCircle,
        color: '#6B7280' // Light gray for skipped questions
      };
    }
    
    // Default fallback (should not reach here)
    return {
      icon: faCircle,
      color: '#6B7280' // Light gray as fallback
    };
  };
    // Get questions for current page
  const startIdx = sidebarPage * questionsPerPage;
  const pageQuestions = questions.slice(startIdx, startIdx + questionsPerPage);
  
  const sidebarStyle = {
    minWidth: isMobile ? 0 : 260,
    maxWidth: isMobile ? 'none' : 280,
    width: isMobile ? '100%' : '280px',
    background: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(249, 250, 251, 0.95)',
    borderRadius: '12px',
    marginRight: isMobile ? 0 : 0,
    boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 16px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    zIndex: 2,
    position: 'relative',
    height: '100%',
    flex: isMobile ? '0 0 auto' : '0 0 280px', // Fixed width instead of flexible growth
    padding: 0,
    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease',
  };

  const headerStyle = {
    fontWeight: 600,
    color: isDarkMode ? '#e2e8f0' : '#334155',
    padding: isMobile ? '12px 0 8px 0' : '16px 0 12px 0',
    fontSize: isMobile ? 14 : 16,
    textAlign: 'center',
    borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
    letterSpacing: '0.3px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  };

  const questionListStyle = {
    flex: 1,
    overflowY: isMobile ? 'hidden' : 'auto',
    overflowX: isMobile ? 'auto' : 'hidden',
    display: 'flex',
    flexDirection: isMobile ? 'row' : 'column',
    gap: isMobile ? 8 : 4,
    padding: isMobile ? '8px' : '8px 6px',
    minHeight: 0,
    maxHeight: '100%',
  };

  // Debug log to verify component is being used
  // console.log('QuestionNavigationSidebar - isDarkMode:', isDarkMode, 'Component loaded at:', new Date().toISOString());
  return (
  <div className="question-navigation-sidebar" style={sidebarStyle}>
      <div style={headerStyle}>
        {/* Close/Pin Handle */}
        <div
          className={`navigation-drag-handle ${isDarkMode ? 'dark-mode' : ''}`}
          onClick={() => {
            // Hide sidebar when clicked
            if (toggleSidebar) {
              toggleSidebar();
            }
          }}
          tabIndex={0}
          role="button"
          aria-label="Click to close sidebar"
          title="Click to close sidebar"
          style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            cursor: 'pointer'
          }}
        >
          <FontAwesomeIcon icon={faTimes} style={{ fontSize: '12px' }} />
        </div>
        Questions
      </div>
      
      {/* Question list */}
      <div style={questionListStyle}>        {pageQuestions.map((question, idx) => {
          const absoluteIdx = startIdx + idx;
          const status = getQuestionStatus(question);
          const isCurrent = question.id === currentQuestionId;
          const preview = question.body?.slice(0, 50) + (question.body?.length > 50 ? '…' : '') || '';
          const response = responses.find(r => r.questionId === question.id);
          const isAnswered = !!(response && response.answerIds && response.answerIds.length > 0);
          const isFlagged = response?.isFlagged;
            return (
            <button
              key={question.id}
              onClick={() => goToQuestion(absoluteIdx)}
              className={`question-nav-button ${isCurrent ? 'current' : ''} ${isAnswered ? 'answered' : ''} ${isFlagged ? 'flagged' : ''} ${isDarkMode ? 'dark-mode' : 'light-mode'} ${isMobile ? 'mobile' : ''}`}
              style={{
                '--status-color': status.color,
              }}
              title={`Go to question ${absoluteIdx + 1}`}
            >
              <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <FontAwesomeIcon icon={status.icon} style={{ color: status.color, fontSize: 18, marginRight: 4 }} />
              </span>              <span 
                className={`question-nav-number ${isCurrent ? 'current' : ''} ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
                style={{
                  '--status-color': status.color,
                }}
              >
                {`${absoluteIdx + 1}-`}
              </span>
              <span 
                className={`question-nav-preview ${isCurrent ? 'current' : ''} ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
                style={{
                  '--status-color': status.color,
                }}
              >
                {preview}
              </span>
              {isFlagged && (
                <FontAwesomeIcon 
                  icon={faFlag} 
                  style={{ 
                    color: '#e53935', 
                    fontSize: 12, 
                    marginLeft: 4,
                    flexShrink: 0 
                  }} 
                />
              )}
            </button>
          );
        })}
      </div>
      
      {/* Pagination controls */}
      {totalSidebarPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 8,
          padding: '8px 0',
          borderTop: isDarkMode ? '1px solid #22304a' : '1px solid #e0e6f0',
          background: 'transparent',
        }}>
          <button 
            onClick={onPrevPage} 
            disabled={sidebarPage === 0}
            className="pt-prev-btn"
            style={{ 
              background: sidebarPage === 0 ? '#ccc' : themeStyles.button.primary.background,
              color: sidebarPage === 0 ? (isDarkMode ? '#fff' : '#2c3e50') : themeStyles.button.primary.text,
              border: 'none', 
              borderRadius: 4, 
              padding: '4px 10px', 
              fontWeight: 600, 
              fontSize: 13, 
              opacity: sidebarPage === 0 ? 0.6 : 1,
              cursor: sidebarPage === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            Prev
          </button>
          <span style={{ fontSize: 13, color: themeStyles.text.secondary }}>
            {sidebarPage + 1} / {totalSidebarPages}
          </span>
          <button 
            onClick={onNextPage} 
            disabled={sidebarPage >= totalSidebarPages - 1}
            className="pt-next-btn"
            style={{ 
              background: sidebarPage >= totalSidebarPages - 1 ? '#ccc' : themeStyles.button.primary.background,
              color: sidebarPage >= totalSidebarPages - 1 ? (isDarkMode ? '#fff' : '#2c3e50') : themeStyles.button.primary.text,
              border: 'none', 
              borderRadius: 4, 
              padding: '4px 10px', 
              fontWeight: 600, 
              fontSize: 13, 
              opacity: sidebarPage >= totalSidebarPages - 1 ? 0.6 : 1,
              cursor: sidebarPage >= totalSidebarPages - 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default QuestionNavigationSidebar;
